import React, { useState, useEffect, createRef, useContext } from 'react';
import { Badge, Button, Card, Container, Col, Form, Row, Spinner } from 'react-bootstrap';
import { FiRefreshCw, FiClock } from 'react-icons/fi';
import { RiAdminLine } from 'react-icons/ri';
import { BsCircleFill } from 'react-icons/bs';
import {
  FaUserCog,
  FaTrashAlt,
  FaRegThumbsUp,
  FaKey
} from 'react-icons/fa';
import MaterialTable, { MTableToolbar } from 'material-table';
import icons from 'MaterialTableIcons';
import {
  addUser,
  getUsers,
  updateUserRole,
  deleteUser,
  resetUserPassword,
  sendInvite,
  getDeploymentConfig,
  getDiscoverySettings,
  saveDiscoverySettings
} from './api';
import AddUserModal from './AddUserModal';
import DeleteUserModal from './DeleteUserModal';
import RoleChangeModal from './RoleChangeModal';
import ResetPasswordModal from './ResetPasswordModal';
import { BiError } from 'react-icons/bi';
import AppContext from 'Utils/AppContext';
import { READ_ONLY_ROLE, roleLabel } from 'Auth/permissions';
import { getErrorMessage } from 'Utils';
import { INK, STATUS } from 'Utils/styleTokens';
import PageHeader from 'Utils/PageHeader';
import DiscoveryTooltip from 'DiscoveryTooltip';
import LicensePanel from 'License/LicensePanel';
import './styles.css';

const columns = [
  { 
    title: 'User', 
    field: 'email' 
  },
  // { 
  //   title: 'Status', 
  //   field: 'status',
  //   render: (rowData) => (
  //     <span className='text-capitalize'>
  //       {rowData.status.replaceAll('_', ' ')}
  //     </span>
  //   )
  // },
  // { 
  //   title: 'Email Verified', 
  //   field: 'emailVerified',
  //   render: (rowData) => (
  //     rowData && rowData.emailVerified ? (
  //       <span className='align-middle'>
  //         <BsCircleFill color='#01d101' className='mr-2' /> Verified
  //       </span>
  //     ) : (
  //       <span className='align-middle'>
  //         <BsCircleFill color='red' className='mr-2' /> Not verified
  //       </span>
  //     )
  //   ) 
  // },
  {
    title: 'Role',
    field: 'role',
    render: (rowData) => (
      <Badge 
        className='pl-3 pr-3 pt-2 pb-2'
        variant={roleBadgeVariant(rowData.role)}
      >
        <span className='text-uppercase'>{roleLabel(rowData.role)}</span>
      </Badge>
    )
  }
];

function roleBadgeVariant(role) {
  const r = (role || '').toUpperCase();
  if (r === 'ADMIN') return 'purple';
  if (r === READ_ONLY_ROLE) return 'secondary';
  return 'primary';
}

const MIN_INTERVAL_MINUTES = 3;

const Admin = (props) => {
  const [fetchingData, setFetchingData] = useState(false);
  const [data, setData] = useState([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [userPendingDelete, setUserPendingDelete] = useState({});
  // Role change (grant/revoke admin) goes through a confirmation dialog (C2D-991).
  // null = closed; otherwise { user, admin: <target boolean> }.
  const [userPendingRoleChange, setUserPendingRoleChange] = useState(null);
  // null = closed; otherwise the user row whose password is being reset.
  const [userPendingPasswordReset, setUserPendingPasswordReset] = useState(null);
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [successUser, setSuccessUser] = useState('');
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');
  const [discoveryEnabled, setDiscoveryEnabled] = useState(true);
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [intervalInput, setIntervalInput] = useState('60');
  const [minIntervalMinutes, setMinIntervalMinutes] = useState(MIN_INTERVAL_MINUTES);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  // Whether the deployment can send email (GET /api/config/deployment). Gates the
  // invite-email mode in AddUserModal and the "email a reset link" option in
  // ResetPasswordModal. Defaults false so a failed fetch preserves today's UI exactly.
  const [emailEnabled, setEmailEnabled] = useState(false);
  const { setNotification } = useContext(AppContext);
  const tableRef = createRef();

  const handleLoadDiscoverySettings = async () => {
    setLoadingSettings(true);
    try {
      const s = await getDiscoverySettings();
      if (s) {
        setDiscoveryEnabled(!!s.enabled);
        setIntervalMinutes(s.intervalMinutes);
        setIntervalInput(String(s.intervalMinutes));
        if (typeof s.minIntervalMinutes === 'number') {
          setMinIntervalMinutes(s.minIntervalMinutes);
        }
      }
    } catch (e) {
      console.log('getDiscoverySettings error', e);
    }
    setLoadingSettings(false);
  };

  const handleSaveDiscoverySettings = async () => {
    setSettingsError('');
    const parsed = parseInt(intervalInput, 10);
    if (!Number.isFinite(parsed) || parsed < minIntervalMinutes) {
      setSettingsError(`Interval must be at least ${minIntervalMinutes} minutes.`);
      return;
    }
    setSavingSettings(true);
    try {
      const saved = await saveDiscoverySettings(discoveryEnabled, parsed);
      if (saved) {
        setDiscoveryEnabled(!!saved.enabled);
        setIntervalMinutes(saved.intervalMinutes);
        setIntervalInput(String(saved.intervalMinutes));
      }
      setNotification({
        header: 'Discovery settings saved',
        icon: (<FaRegThumbsUp size='25px' color={STATUS.success.dot} />),
        body: (
          <span>
            {saved && !saved.enabled
              ? 'Automatic discovery is now disabled. Any in-flight scheduled scan will finish, after which no further scans will run until re-enabled.'
              : `Changes take effect on the next scheduled refresh (up to ${saved ? saved.intervalMinutes : parsed} minutes from now). The currently scheduled tick will use the previous settings.`}
          </span>
        )
      });
    } catch (e) {
      const msg = getErrorMessage(e) || 'Failed to save settings';
      setSettingsError(msg);
      setNotification({
        header: 'Error saving settings',
        icon: (<BiError size='25px' color={INK[500]} />),
        body: <span>{msg}</span>
      });
    }
    setSavingSettings(false);
  };
  
  const handleGetUsers = async () => {
    setFetchingData(true);

    try {
      const dataValue = await getUsers();
      setData(dataValue);
    } catch(e) {
      console.log(e);
    }

    setFetchingData(false);
  }

  const handleAddUser = async (email, password, role) => {
    console.log('HANDLE ADD USER', email, password, role)
    setFetchingData(true);

    try {
      const created = await addUser(email, password, role );
      await handleGetUsers();

      setShowAddUserModal(false);
      if (!password && created && created.inviteEmailSent === false) {
        // User exists, but the invite email could not be delivered — the admin
        // should fall back to setting a temporary password.
        setNotification({
          header: 'User created, but the invite email failed',
          icon: (<BiError size='25px' color={INK[500]} />),
          body: (
            <span>
              The invite email to {email} could not be sent. Use "Reset password"
              on the user to set a temporary password instead.
            </span>
          )
        });
      } else {
        setNotification({
          header: 'Successfully added user!',
          icon: (<FaRegThumbsUp size='25px' color={STATUS.success.dot} />),
          body: (
            <span>
              {!password && created && created.inviteEmailSent
                ? `User saved. An invite email was sent to ${email} so they can set their password.`
                : 'User successfully saved.'}
            </span>
          )
        });
      }
      setError('');
    } catch (error) {
      setNotification({
        header: 'Error saving user',
        icon: (<BiError size='25px' color={INK[500]} />),
        body: <span>{getErrorMessage(error)}</span>
      });
    }

    setFetchingData(false);
  }

  const handleDeleteUser = async (user) => {
    setFetchingData(true);

    try {
      await deleteUser(user.email);
      await handleGetUsers();
      
      setError('');
      setInfo(`Successfully deleted ${user.email}`);
    } catch (error) {
      setError(getErrorMessage(error));
    }

    setFetchingData(false);
  }

  const handleChangeRole = async (user, role) => {
    setFetchingData(true);
    const infoValue = `${user.email} is now ${roleLabel(role)}`;

    try {
      await updateUserRole(user, role);
      await handleGetUsers(); 

      setInfo(infoValue);
      setError('');
    } catch (error) {
      setError(getErrorMessage(error));
    }

    setFetchingData(false);
  }

  const handleResetPassword = async (user, newPassword) => {
    setFetchingData(true);

    try {
      await resetUserPassword(user.id, newPassword);

      setResetPasswordError('');
      setUserPendingPasswordReset(null);
      setNotification({
        header: 'Password reset',
        icon: (<FaRegThumbsUp size='25px' color={STATUS.success.dot} />),
        body: (
          <span>
            Password for {user.email} was reset. They will be asked to choose a
            new password at their next login.
          </span>
        )
      });
    } catch (error) {
      setResetPasswordError(getErrorMessage(error));
    }

    setFetchingData(false);
  }

  useEffect(() => {
    handleGetUsers();
    handleLoadDiscoverySettings();
    // Email features stay hidden if the deployment flag can't be read.
    getDeploymentConfig()
      .then((deployment) => setEmailEnabled(deployment?.emailEnabled === true))
      .catch((e) => console.log('deployment config fetch failed; email features hidden', e));
  }, []);

  return (
    <Col className='p-0'>
      <Container fluid>
        <Container>
          <PageHeader
            header='User Management'
            icon={RiAdminLine}
            tooltip='Create or manage users.'
          />
          <Row className='mb-3'>
            <Col>
              <Card>
                <div className='entity-panel-table-header d-flex'>
                  <div className='text-left widget-header p-3'>
                    <FiClock className='mr-2 widget-header-icon' />
                    <span>Cloud Discovery Refresh</span>
                  </div>
                </div>
                <Card.Body>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSaveDiscoverySettings();
                    }}
                  >
                    <Row className='align-items-end'>
                      <Col xs='auto'>
                        <Form.Check
                          type='switch'
                          id='discovery-enabled-switch'
                          label={discoveryEnabled ? 'Enabled' : 'Disabled'}
                          checked={discoveryEnabled}
                          disabled={loadingSettings || savingSettings}
                          onChange={(e) => setDiscoveryEnabled(e.target.checked)}
                        />
                      </Col>
                      <Col xs='auto'>
                        <Form.Label className='mb-1'>Refresh interval (minutes)</Form.Label>
                        <Form.Control
                          type='number'
                          min={minIntervalMinutes}
                          step={1}
                          style={{ width: '8rem' }}
                          value={intervalInput}
                          disabled={!discoveryEnabled || loadingSettings || savingSettings}
                          onChange={(e) => setIntervalInput(e.target.value)}
                        />
                      </Col>
                      <Col xs='auto'>
                        <Button
                          type='submit'
                          variant='primary'
                          disabled={loadingSettings || savingSettings}
                        >
                          {savingSettings ? (
                            <Spinner
                              className='mr-2'
                              as='span'
                              animation='border'
                              size='sm'
                              role='status'
                              aria-hidden='true'
                            />
                          ) : null}
                          SAVE
                        </Button>
                      </Col>
                      <Col className='text-muted small'>
                        Minimum {minIntervalMinutes} minutes. Current saved value: {intervalMinutes} min
                        {discoveryEnabled ? '' : ' (disabled)'}.
                      </Col>
                    </Row>
                    {settingsError ? (
                      <Row>
                        <Col className='text-danger small mt-2'>{settingsError}</Col>
                      </Row>
                    ) : null}
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className='mb-3'>
            <Col>
              <LicensePanel version={props.config?.VERSION} />
            </Col>
          </Row>
          <Row>
            <Col>
              <Card>
                <MaterialTable
                  isLoading={fetchingData}
                  tableRef={tableRef}
                  actions={[{}]} // actions needed an empty value to populate actions column with component
                  columns={columns}
                  components={{
                    Toolbar: props => (
                      <div className="entity-panel-table-header d-flex">
                        <div className="text-left widget-header p-3">
                          <FaUserCog 
                            className="mr-2 widget-header-icon" 
                            />
                          <span>Add / Manage Users </span>
                        </div>
                        <div className='d-flex flex-grow-1 justify-content-end'>
                          <MTableToolbar { ...props } />
                          <Button
                            onClick={async () => await handleGetUsers()}
                            className='ml-0 mr-2 pl-4 pr-4'
                            variant='outline-secondary'
                            disabled={fetchingData}
                            type='button'
                          >
                            { fetchingData ? (
                              <Spinner
                                className='mr-2'
                                as='span'
                                animation='border'
                                size='sm'
                                role='status'
                                aria-hidden='true'
                              />
                            ) : (
                              <FiRefreshCw className='mr-2' />
                            )}
                            REFRESH
                          </Button>
                          <Button
                            onClick={() => setShowAddUserModal(!showAddUserModal)}
                            className='ml-0 mr-0 pl-4 pr-4'
                            variant='primary'
                            type='button'
                          >
                            + ADD
                          </Button>
                        </div>
                      </div>
                    ),
                    Action: props => (
                      <div>
                        <DiscoveryTooltip text='Change role' placement='top'>
                          <FaUserCog
                            size='1rem'
                            data-testid="admin-toggle-role"
                            onClick={() => setUserPendingRoleChange({ user: props.data })}
                            className='ml-2 mr-2 admin-icon'
                          />
                        </DiscoveryTooltip>

                        <DiscoveryTooltip text='Reset password' placement='top'>
                          <FaKey
                            className='ml-2 mr-2 admin-icon'
                            data-testid="admin-reset-password"
                            onClick={() => {
                              setResetPasswordError('');
                              setUserPendingPasswordReset(props.data);
                            }}
                          />
                        </DiscoveryTooltip>

                        <DiscoveryTooltip text='Delete user' placement='top'>
                          <FaTrashAlt
                            className='ml-2 mr-2 trash-icon'
                            data-testid="admin-delete-user"
                            onClick={() => {
                              setUserPendingDelete(props.data);
                              setShowDeleteUserModal(true);
                            }}
                          />
                        </DiscoveryTooltip>
                      </div>
                    )
                  }}
                  data={data}
                  icons={icons}
                  localization={{
                    header: {
                      actions: 'MODIFY'
                    }
                  }}
                  options={{
                    actionsColumnIndex: -1,
                    showTitle: false,
                    search: true,
                    pageSize: 10
                  }}
                />
              </Card>
            </Col>
          </Row>
          <AddUserModal
            error={error}
            emailEnabled={emailEnabled}
            users={data}
            handleClose={() => setShowAddUserModal(false)}
            handleAddUser={async (value, password, role) => await handleAddUser(value, password, role)}
            showAddUserModal={showAddUserModal}
          />
          <DeleteUserModal
            error={error}
            handleClose={() => setShowDeleteUserModal(false)}
            handleDeleteUser={async (obj) => await handleDeleteUser(obj)}
            showDeleteUserModal={showDeleteUserModal}
            userPendingDelete={userPendingDelete}
          />
          <RoleChangeModal
            show={userPendingRoleChange != null}
            userPendingRoleChange={userPendingRoleChange}
            handleClose={() => setUserPendingRoleChange(null)}
            handleConfirm={async (user, role) => await handleChangeRole(user, role)}
          />
          <ResetPasswordModal
            show={userPendingPasswordReset != null}
            user={userPendingPasswordReset}
            error={resetPasswordError}
            emailEnabled={emailEnabled}
            handleClose={() => setUserPendingPasswordReset(null)}
            handleConfirm={async (user, newPassword) => await handleResetPassword(user, newPassword)}
            handleSendInvite={async (user) => await sendInvite(user.id)}
          />
        </Container>
      </Container>
    </Col>
  )
};

export default Admin;